import React, { useState } from 'react';
import { useRateio } from '../hooks/useRateio';
import { fmtCompetencia } from '../utils/formatters';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import {
  ComRateio,
  TEXTO_DA_BASE,
  TEXTO_DO_CRITERIO,
  avisosDoRateio,
  ehVersaoAtual,
  fmtPercentual,
  poolDaVersao,
  versoesParaSeletor,
} from '../utils/rateioDoGrupo';

const brl = (v: number) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * "COMO ESTÁ SENDO DIVIDIDA": o rateio do grupo, lido da API.
 *
 * Responde o que não existia em lugar nenhum: o que é compartilhado, quanto
 * custa, qual regra divide (critério + base) e a fatia de cada empresa, somando
 * 100%. Só leitura: a tela de EDIÇÃO do rateio depende da etapa de aplicar o
 * recálculo, que o backend deixou de fora de propósito (reescreve dinheiro em
 * 12 itens de 6 contratos e precisa de confirmação humana).
 *
 * Serve a partir de QUALQUER membro do grupo (a API resolve a matriz).
 */
export default function PainelDoRateio({
  clientId,
  nome,
  destacar,
  ocultarSemRateio,
  semCompartilhado,
  aguardarEscolha,
}: {
  clientId: number;
  nome: (clientId: number) => string;
  /** Empresa a destacar na lista (a da assinatura aberta). */
  destacar?: number;
  /** Na tela de grupos: grupo sem rateio não mostra nada (é o caso da FESTVAL). */
  ocultarSemRateio?: boolean;
  /** Na tela de grupos o "O que o grupo compartilha" vive no ListasDoRateio: não repete aqui. */
  semCompartilhado?: boolean;
  /** Aba Histórico da gaveta do rateio (decisão do Heitor, 10/10/2026): abre SEM versão escolhida, só o seletor e a
   *  frase; a tabela por empresa aparece depois da escolha. A vigente mora na aba Divisão, e repeti-la aqui confundia. */
  aguardarEscolha?: boolean;
}) {
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [escolheu, setEscolheu] = useState(false);
  // Mesmo cache da ficha, das listas e do "quem usa, quem paga" (hooks/useRateio).
  const { rateio: r, erro, carregando } = useRateio(clientId, competencia);

  if (carregando && !r) return ocultarSemRateio ? null : <div className='text-sm text-slate-500 py-3'>Lendo o rateio do grupo…</div>;
  if (erro) return <div className='text-sm text-red-300 py-3'>{erro}</div>;
  if (!r) return null;
  if (r.temRateio === false) {
    if (ocultarSemRateio && !competencia) return null;
    return <div className='text-sm text-slate-500 py-3'>Sem rateio{competencia ? ` em ${fmtCompetencia(competencia)}` : ''}: {r.motivo}.</div>;
  }

  const rr = r as ComRateio;
  const atual = ehVersaoAtual(rr);
  const pool = poolDaVersao(rr);
  const avisos = avisosDoRateio(rr, nome);
  const versoes = versoesParaSeletor(rr.versoes || []);
  const ramaisDe = (id: number) => rr.base.find((b) => b.clientId === id)?.ramais;
  const seletorDeVersoes = (comDestaque: boolean) => (
      <div className='flex items-center gap-1.5 flex-wrap text-xs'>
        <span className='text-slate-500 mr-1'>Versão:</span>
        {versoes.map((v) => {
          const ativa = comDestaque && v.id === rr.versaoId;
          return (
            <button
              key={v.id}
              type='button'
              disabled={!v.atual && !v.competenciaParaVer}
              onClick={() => { setCompetencia(v.competenciaParaVer); setEscolheu(true); }}
              title={`${TEXTO_DO_CRITERIO[v.criterio]}, ${TEXTO_DA_BASE[v.baseDoPercentual]}`}
              className={`px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${ativa ? `${TONE_BADGE.group} ${TONE_BORDER.group}` : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
            >
              {v.atual ? 'atual' : 'anterior'}{' · '}{v.competenciaInicio ? `desde ${fmtCompetencia(v.competenciaInicio)}` : 'início não registrado'}
            </button>
          );
        })}
      </div>
  );

  if (aguardarEscolha && !escolheu) {
    return (
      <div className='space-y-2' data-rateio='historico-sem-escolha'>
        {versoes.length > 1 ? (
          <>
            {seletorDeVersoes(false)}
            <p className='text-sm text-slate-400'>Escolha uma versão para ver como valia. A vigente está na aba Divisão.</p>
          </>
        ) : (
          <p className='text-sm text-slate-400'>Só há a versão vigente, e ela está na aba Divisão.</p>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {/* Versões: refaturar um mês antigo precisa da divisão DAQUELE mês. */}
      {versoes.length > 1 && seletorDeVersoes(true)}

      {avisos.map((a, i) => (
        <div key={i} className={`rounded-lg border px-3 py-2 text-sm ${a.gravidade === 'erro' ? 'bg-red-500/10 border-red-600/40 text-red-200' : 'bg-amber-500/10 border-amber-600/40 text-amber-200'}`}>
          <p className='font-medium'>{a.gravidade === 'erro' ? '⚠ ' : ''}{a.titulo}</p>
          {a.linhas.map((l, j) => <p key={j} className='text-xs mt-0.5 opacity-90'>{l}</p>)}
          {a.gravidade === 'proposta' && (
            <p className='text-xs mt-1 opacity-70'>É uma proposta: a divisão cobrada não muda sem confirmação, e a tela de aplicar ainda não existe.</p>
          )}
        </div>
      ))}

      {/* Versão que não registrou o que compunha o pool e a base: o motivo, NUNCA as
          listas de hoje no lugar das daquela época (contrato de 05/10/2026). */}
      {rr.listasRegistradas === false && (
        <p className='text-xs text-slate-400 rounded-lg border border-slate-700 px-3 py-2' data-testid='listas-nao-registradas'>
          {rr.listasNaoRegistradasMotivo || 'Esta versão não registrou quais produtos compunham o compartilhado e a base.'}
        </p>
      )}

      {/* O que é compartilhado, com o valor cheio (só na versão atual: o detalhe
          por item é dos contratos de hoje). */}
      {!semCompartilhado && atual && rr.listasRegistradas !== false && rr.itens.vigente.length > 0 && (
        <div className='rounded-lg border border-slate-700 overflow-hidden'>
          <div className='flex items-center gap-2 px-3 py-2 bg-slate-800/80 text-sm'>
            <span className='font-medium text-white'>O que o grupo compartilha</span>
            <span className='ml-auto font-semibold text-green-400 tabular-nums'>{brl(rr.pool)}</span>
          </div>
          {rr.itens.vigente.map((it) => (
            <div key={it.productId} className='flex items-center gap-2 px-3 py-1.5 border-t border-slate-800 text-sm'>
              <span className='text-slate-300 min-w-0 truncate'>{it.name || it.productCode}</span>
              {it.productCode && <span className='text-[11px] text-slate-500 font-mono'>{it.productCode}</span>}
              <span className={`ml-auto tabular-nums ${it.valorCheio < 0 ? 'text-amber-300' : 'text-slate-200'}`}>{brl(it.valorCheio)}</span>
            </div>
          ))}
        </div>
      )}

      <div className='rounded-lg border border-slate-700 overflow-hidden'>
        <div className='flex items-center gap-2 flex-wrap px-3 py-2 bg-slate-800/80 text-sm'>
          <span className='font-medium text-white'>Como está sendo dividida</span>
          <span className='text-xs text-slate-400'>
            {TEXTO_DO_CRITERIO[rr.criterio]}, {TEXTO_DA_BASE[rr.baseDoPercentual]}
            {rr.competenciaInicio ? ` · desde ${fmtCompetencia(rr.competenciaInicio)}` : ''}
          </span>
          <span className='ml-auto font-semibold text-green-400 tabular-nums'>{brl(pool)}</span>
        </div>
        {rr.linhas.map((l) => {
          const ram = ramaisDe(l.clientId);
          return (
            <div key={l.clientId} className={`flex items-center gap-3 flex-wrap px-3 py-2 border-t border-slate-800 text-sm ${destacar === l.clientId ? TONE_SURFACE.group : ''}`}>
              <span className='text-slate-200 min-w-0 truncate flex-1'>
                {nome(l.clientId)}
                {l.clientId === rr.matrizId && <span className='ml-1.5 text-[11px] text-slate-500'>· matriz</span>}
              </span>
              {rr.criterio === 'ramais_contratados' && ram != null && <span className='text-xs text-slate-500 tabular-nums'>{ram} {ram === 1 ? 'ramal' : 'ramais'}</span>}
              <span className='hidden sm:block w-32 h-1.5 rounded bg-slate-700 overflow-hidden'>
                <span className='block h-full bg-violet-400' style={{ width: `${Math.max(0, Math.min(100, l.percentualVigente))}%` }} />
              </span>
              <span className={`w-16 text-right tabular-nums ${TONE_TEXT.group}`}>{fmtPercentual(l.percentualVigente)}</span>
              {atual && <span className='w-24 text-right text-green-400 tabular-nums' title='Acordado na versão'>{l.valorAcordado != null ? brl(l.valorAcordado) : '—'}</span>}
            </div>
          );
        })}
        <div className='flex items-center gap-3 px-3 py-2 border-t border-slate-700 bg-green-500/5 text-sm font-medium'>
          <span className='flex-1 text-slate-200'>Total</span>
          {rr.criterio === 'ramais_contratados' && <span className='text-xs text-slate-500 tabular-nums'>{rr.base.reduce((s, b) => s + b.ramais, 0)} ramais</span>}
          <span className={`w-16 text-right tabular-nums ${rr.somaVigenteFecha ? 'text-green-400' : 'text-red-300'}`}>{fmtPercentual(rr.somaVigente)}</span>
          {atual && <span className='w-24 text-right text-green-400 tabular-nums' title='Acordado na versão'>{rr.linhas.every((l) => l.valorAcordado != null) ? brl(rr.linhas.reduce((s, l) => s + Number(l.valorAcordado), 0)) : '—'}</span>}
        </div>
      </div>

      {!atual && (
        <p className='text-xs text-slate-500'>
          Versão histórica: percentuais e base como valiam desde {fmtCompetencia(rr.competenciaInicio)}, sobre {brl(pool)}. Os valores em reais por empresa daquela época não estão guardados por item.
        </p>
      )}
      {rr.observacao && <p className='text-xs text-slate-500 whitespace-pre-line'>{rr.observacao}</p>}
    </div>
  );
}
